import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useLogger } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { useLicenses } from '~Providers/LicensesProvider';
import { useGiftCardsContext } from '~Scenes/GiftCards/GiftCardsProvider';
import { StageLayer } from '../../styles';
import { useExpressOrder } from '../../Providers/ExpressOrderProvider';
import { useExpressCart } from '../../Providers/ExpressCartProvider';
import { useEposTheme } from '../../Providers/ThemeProvider';
import LegalAgeStep from './LegalAgeStep';
import ReceiptStep from './ReceiptStep';
import PaymentMethodStep from './PaymentMethodStep';
import PaymentStep from './PaymentStep';
import DoneStep from './DoneStep';

/** Lets the menu sheet get most of the way down before the card comes up. */
const ENTER_DELAY_MS = 180;

/** A gift card charge is often instant - the loading state stays up at least this long anyway. */
const GIFT_CARD_MIN_LOADING_MS = 2000;

/**
 * Transparent, like every other layer on the stage - the one photo painted
 * on `Stage` (styles/Stage.tsx) stays mounted and unchanged behind it for
 * the whole flow, so checkout never needs a backdrop of its own. Giving this
 * layer its own fill (solid or a redrawn copy of the photo) repaints a
 * second full-screen background in the same frame as the menu's exit slide
 * and the card's enter animation, which is heavy enough to stall the
 * transition on kiosk hardware - don't reintroduce one.
 */
const Layer = styled(StageLayer)`
  align-items: center;
  justify-content: center;
  padding: 48px;
  box-sizing: border-box;
`;

/**
 * The checkout card, floating over the photo background. One card for the
 * whole checkout; the steps swap inside it, so the shopper keeps their frame
 * of reference while the menu is gone.
 */
const Card = styled.div`
  width: min(100%, 1040px);
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 40px;
  background: var(--eposSurface);
  color: var(--eposText);
  font-family: var(--eposFontFamily);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  animation: eposCheckoutIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) ${ENTER_DELAY_MS}ms both;
  will-change: transform, opacity;

  @keyframes eposCheckoutIn {
    from {
      opacity: 0;
      transform: translate3d(0, 48px, 0) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0) scale(1);
    }
  }
`;

interface Props {
  /** Navigation only: the shopper backed out before paying - Menu comes back. */
  onBack: () => void;
  /** Navigation only: the order is finished - back to the welcome screen. */
  onDone: () => void;
}

/**
 * (Age check ->) receipt question -> terminal -> order number. The order
 * itself lives in ExpressOrderProvider; this scene only asks and shows.
 * Mounted by EposFlow after the menu sheet has slid away.
 */
const Checkout: FC<Props> = ({ onBack, onDone }) => {
  const logger = useLogger('epos-checkout');
  const [storage] = useStorage();
  const eposTheme = useEposTheme();
  const expressOrder = useExpressOrder();
  /** The cart cannot change while the checkout is open, so this never re-renders us. */
  const expressCart = useExpressCart();
  const licenses = useLicenses();
  const giftCardsContext = useGiftCardsContext();

  /** Set once the shopper has accepted that ID may be checked for 18+ items. */
  const [ageConfirmed, setAgeConfirmed] = useState<boolean>(false);
  const needsAgeCheck = expressCart.hasAgeRestrictedItems && !ageConfirmed;

  /** Card or gift card is asked only with the gift card license (and its universe loaded). */
  const asksPaymentMethod = licenses.hasGiftcard && !!giftCardsContext.universeId;
  /** The receipt answer, held while the payment method is asked. Null = not answered yet. */
  const [receiptChoice, setReceiptChoice] = useState<boolean | null>(null);

  /** Receipt answered - straight to the terminal, or first "card or gift card?". */
  const handleReceiptChoice = useCallback(
    (wantsReceipt: boolean) => {
      if (!asksPaymentMethod) {
        expressOrder.startPayment(wantsReceipt);
        return;
      }
      logger.info('Gift card license - asking for payment method', { wantsReceipt });
      setReceiptChoice(wantsReceipt);
    },
    [asksPaymentMethod, expressOrder.startPayment],
  );

  // Create the order once the shopper is past the age check (or never needed it).
  useEffect(() => {
    if (needsAgeCheck) {
      logger.info('Cart holds age-restricted items - asking before creating the order');
      return;
    }
    if (expressOrder.phase === 'idle') {
      expressOrder.begin();
    }
  }, [needsAgeCheck]);

  /**
   * Devices set to always print, or a venue configured to only print
   * receipts, never see the receipt question - the order is created and the
   * terminal asked in one go, assuming "yes".
   */
  useEffect(() => {
    if (
      expressOrder.phase === 'receipt' &&
      (storage.alwaysPrintReceiptEPOS || eposTheme.onlyPrintReceipt)
    ) {
      logger.info('Receipt forced by device or venue setting - skipping the question');
      handleReceiptChoice(true);
    }
  }, [expressOrder.phase, storage.alwaysPrintReceiptEPOS, eposTheme.onlyPrintReceipt]);

  /** Kept on the order through a failure, unlike the payment - the failed screen stays a gift card one. */
  const paidByGiftCard = expressOrder.order?.method === 'BAEMINGO_GIFTCARD';
  /** A failed gift card retries from "card or gift card?", not silently on the terminal. */
  const handleRetryChoice = useCallback(() => expressOrder.reopenPaymentChoice(), [
    expressOrder.reopenPaymentChoice,
  ]);

  /**
   * True for GIFT_CARD_MIN_LOADING_MS after a gift card charge starts. The
   * provider moves on as soon as the charge settles; only what is shown waits.
   * The timer lives in a ref, so the phase leaving "paying" does not cancel it.
   */
  const [holdingGiftCard, setHoldingGiftCard] = useState(false);
  const holdTimerRef = useRef<number | undefined>(undefined);
  const giftCardPaying = expressOrder.phase === 'paying' && paidByGiftCard;
  useEffect(() => {
    if (!giftCardPaying) {
      return;
    }
    logger.info('Gift card charge started - holding the loading state', {
      minimumMs: GIFT_CARD_MIN_LOADING_MS,
    });
    setHoldingGiftCard(true);
    window.clearTimeout(holdTimerRef.current);
    holdTimerRef.current = window.setTimeout(() => {
      logger.info('Gift card minimum loading time passed');
      setHoldingGiftCard(false);
    }, GIFT_CARD_MIN_LOADING_MS);
  }, [giftCardPaying]);
  useEffect(() => () => window.clearTimeout(holdTimerRef.current), []);

  const handleBack = useCallback(() => {
    expressOrder.cancel();
    onBack();
  }, [expressOrder.cancel, onBack]);

  const handleAgeConfirmed = useCallback(() => {
    logger.info('Shopper confirmed 18+ for age-restricted items');
    setAgeConfirmed(true);
  }, []);

  const handleFinish = useCallback(() => {
    expressOrder.finish();
    onDone();
  }, [expressOrder.finish, onDone]);

  const renderStep = () => {
    if (needsAgeCheck) {
      return <LegalAgeStep onContinue={handleAgeConfirmed} onBack={handleBack} />;
    }
    /** Charge already settled but the minimum is not up - keep showing it as loading. */
    if (holdingGiftCard && paidByGiftCard && expressOrder.phase !== 'receipt') {
      return (
        <PaymentStep
          failed={false}
          giftCard
          paymentStatus="PENDING"
          onRetry={handleRetryChoice}
          onBack={handleBack}
          onAbort={expressOrder.abortPayment}
        />
      );
    }
    switch (expressOrder.phase) {
      case 'paying':
      case 'failed':
        return (
          <PaymentStep
            failed={expressOrder.phase === 'failed'}
            giftCard={paidByGiftCard}
            paymentStatus={expressOrder.payment?.status}
            onRetry={paidByGiftCard ? handleRetryChoice : expressOrder.startPayment}
            onBack={handleBack}
            onAbort={expressOrder.abortPayment}
          />
        );
      case 'done':
        return <DoneStep orderNumber={expressOrder.orderNumber} onFinish={handleFinish} />;
      default:
        if (receiptChoice !== null) {
          return (
            <PaymentMethodStep
              wantsReceipt={receiptChoice}
              onCard={expressOrder.startPayment}
              onBack={handleBack}
            />
          );
        }
        return <ReceiptStep onChoose={handleReceiptChoice} onBack={handleBack} />;
    }
  };

  return (
    <Layer data-testid="epos-checkout">
      <Card>{renderStep()}</Card>
    </Layer>
  );
};

export default memo(Checkout);
